import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ProductDataComponent } from '../product-data/product-data.component';
import { LegalChecksComponent } from '../legal-checks/legal-checks.component';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InsuranceDetailsComponent } from '@base/components/insurance-details/insurance-details.component';
import { PersonDataComponent } from '@base/components/person-data/person-data.component';
import { PaymentDataComponent } from '@base/components/payment-data/payment-data.component';
import { checkForPaymentInfoInUrl, checkForPersonDataInUrl } from '@base/app/shared/utils/check-for-data-in-url.helper';
import { createGarantieForm, GarantieFormModel } from '../../factories/garantie-form.factory';
import { GarantieService } from '../../services/garantie.service';
import {
  BrokernetLanguageType,
  BrokernetProductName, Bundesland,
  PaymentType,
  PublicPersonCreateDto,
  PublicServoGarantieContractCreateDto,
  PublicServoGarantieLegalChecksDto, ServoGarantieVersicherungspraemieCalcDto
} from '@base/backend';
import { getAdvertiserInfo, getPublicLinkCustomerDto } from '@base/app/shared/utils/dto-builder.helper';
import { ServoProductNameTranslations } from '@base/models/enum/insurance-type.enum';

@Component({
  selector: 'app-garantie-form-wrapper',
  standalone: true,
  imports: [
    ProductDataComponent,
    InsuranceDetailsComponent,
    PersonDataComponent,
    PaymentDataComponent,
    LegalChecksComponent,
    LegalChecksComponent,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './garantie-form-wrapper.component.html',
  styleUrl: './garantie-form-wrapper.component.scss'
})
export class GarantieFormWrapperComponent implements OnInit {
  readonly garantieService = inject(GarantieService);

  protected readonly InsuranceType = ServoProductNameTranslations;

  @Input() isPremiumSchutz!: boolean;
  @Input() premium!: number;
  @Input() servoAdvertiserId: string | null = null;
  @Input() isPublic: boolean = false;

  @Output() submitted = new EventEmitter<void>();

  isLoading = false;

  form!: FormGroup<GarantieFormModel>;

  ngOnInit() {
    this.form = createGarantieForm();

    const isPremiumSchutzForm = this.form.controls.product.controls.isPremiumSchutz;
    isPremiumSchutzForm.setValue(this.isPremiumSchutz, { emitEvent: false });

    this.checkForDataInUrl();

    isPremiumSchutzForm.valueChanges
      .subscribe(isPremiumSchutz => {
        this.garantieService.getPremium(isPremiumSchutz ?? false).subscribe((result) => {
          if (result) {
            this.premium = result;
          } else {
            this.premium = 0;
          }
        });
      });
  }

  checkForDataInUrl(): void {
    const url: URL = new URL(window.location.href);
    const personForm = this.form.controls.person.controls;
    const paymentForm = this.form.controls.payment.controls;

    checkForPersonDataInUrl(url, personForm);
    checkForPaymentInfoInUrl(url, paymentForm);
  }

  submit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;

    const dto: PublicServoGarantieContractCreateDto = {
      publicPerson: this.isPublic ? this.getPublicPersonCreateDto() : undefined,
      publicServoGarantieVersicherungspraemieCalcDto: this.getProductDetailsDto(),
      publicLegalCheck: this.getLegalChecksDto(),
      publicBrokerAdvertiserInfo: this.isPublic ? getAdvertiserInfo(this.servoAdvertiserId) : undefined,
      publicLinkCustomerDto: this.isPublic ? undefined : getPublicLinkCustomerDto()
    };

    this.garantieService.submitGarantieContract(dto)
      .subscribe({
        next: () => this.submitted.emit(),
        complete: () => this.isLoading = false
      });
  }

  private getPublicPersonCreateDto(): PublicPersonCreateDto {
    const rawPersonData = this.form.controls.person.getRawValue();
    const paymentData = this.form.controls.payment.getRawValue();

    return {
      firstname: rawPersonData.firstname!,
      lastname: rawPersonData.lastname!,
      publicAddressCreateDto: {
        city: rawPersonData.address.city!,
        country: rawPersonData.address.country!,
        houseNumber: rawPersonData.address.houseNumber!,
        street: rawPersonData.address.street!,
        zip: rawPersonData.address.zip!,
        bundesland: Bundesland.Oberoesterreich
      },
      publicContactInfoCreateDto: {
        email: rawPersonData.email!,
        phone: rawPersonData.phone
      },
      publicPaymentInfoCreateDto: {
        iban: paymentData.iban!,
        paymentPeriod: paymentData.paymentPeriod!,
        paymentType: PaymentType.Sepa
      },
      brokernetLanguageType: BrokernetLanguageType.De
    }
  }

  private getProductDetailsDto(): ServoGarantieVersicherungspraemieCalcDto {
    return {
      isPremiumSchutzSelected: this.form.controls.product.controls.isPremiumSchutz.value!
    }
  }

  private getLegalChecksDto(): PublicServoGarantieLegalChecksDto {
    const rawLegalChecksData = this.form.controls.legalCheck.getRawValue();

    return {
      hasAgreedToElectronicCommunication: rawLegalChecksData.hasAgreedToElectronicCommunication,
      hasAcceptedPrivacyPolicy: rawLegalChecksData.hasAcceptedPrivacyPolicy,
      hasAcceptedGeneralTermsAndConditions: rawLegalChecksData.hasAcceptedGeneralTermsAndConditions,
      hasReadInsuranceProductInformationSheet: rawLegalChecksData.hasReadInsuranceProductInformationSheet,
      hasAuthorizedSepaDirectDebit: rawLegalChecksData.hasAuthorizedSepaDirectDebit,
      hasAcknowledgedRightOfWithdrawal: rawLegalChecksData.hasAcknowledgedRightOfWithdrawal,
      hasConfirmedProductMeetsWishesAndNeeds: rawLegalChecksData.hasConfirmedProductMeetsWishesAndNeeds
    }
  }

  protected readonly BrokernetProductName = BrokernetProductName;
}
